// Тялото на общия потвърждаващ диалог — еднакво в декларативния `ConfirmDialog`
// и в `useConfirm` (там го рисува `modal.confirm` през `modalRender`, затова
// панелът носи собствения си фон, заоблени ъгли и сянка).
//
// Анатомия: [чип с икона в цвета на намерението] заглавие-въпрос + едно
// изречение „какво ще стане" → преглед на последствията → избори-карти / поле
// → бележки за обратимостта → футър: подсказка за клавишите (само десктоп) ·
// Отказ · конкретният глагол (danger = червен бутон).
//
// Фокус: danger → „Отказ" (случаен Enter не трие); иначе основният бутон или
// основният избор. Клавишите — `useConfirmKeyboard`.
import { useRef, useState, type ReactNode } from "react";
import { ExclamationCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { isEditableTarget } from "../../../../lib/overlay";
import type {
  ConfirmChoice,
  ConfirmContent,
  ConfirmInitialFocus,
  ConfirmIntent,
} from "./confirmTypes";
import { ChoiceCards, ConfirmFooter, NoteLine, PreviewList } from "./ConfirmParts";
import { ACTION_ATTR, useConfirmKeyboard } from "./useConfirmKeyboard";

const CHIP_CLASS: Record<ConfirmIntent, string> = {
  info: "bg-brand-tint text-brand-deep-ink",
  warning: "bg-warn-tint text-warn-ink",
  danger: "bg-danger-tint text-danger-ink",
};
const DEFAULT_ICON: Record<ConfirmIntent, ReactNode> = {
  info: <QuestionCircleOutlined />,
  warning: <ExclamationCircleOutlined />,
  danger: <ExclamationCircleOutlined />,
};

export type ConfirmPanelProps = ConfirmContent & {
  // false, щом диалогът е затворен или се затваря (клавишите вече не са негови)
  isActive: () => boolean;
  busy?: boolean;
  initialFocus?: ConfirmInitialFocus;
  choices?: ConfirmChoice[];
  // допълнително поле в тялото (причината при анулиране)
  children?: ReactNode;
  confirmDisabled?: boolean;
  // без бутон „Отказ" (изборите-карти са целият въпрос); Esc пак затваря
  hideCancel?: boolean;
  onConfirm?: () => void;
  onCancel: () => void;
};

export const ConfirmPanel = ({
  intent,
  icon,
  title,
  explanation,
  preview,
  note,
  confirmLabel,
  cancelLabel,
  isActive,
  busy = false,
  initialFocus,
  choices,
  children,
  confirmDisabled = false,
  hideCancel = false,
  onConfirm,
  onCancel,
}: ConfirmPanelProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isDesktop } = useViewport();
  const panelRef = useRef<HTMLDivElement>(null);
  const danger = intent === "danger";
  const cancelText = cancelLabel ?? t("inventoryPage.cancel");
  const usableChoices = choices?.filter((choice) => !choice.disabled) ?? [];
  const primaryChoice = usableChoices.find((choice) => choice.primary) ?? usableChoices[0];

  const focus: ConfirmInitialFocus =
    initialFocus ??
    (primaryChoice
      ? { choice: primaryChoice.key }
      : danger || !confirmLabel
        ? "cancel"
        : "confirm");
  // Enter, когато фокусът не е на бутон: от полето за писане → безопасният
  const defaultAction = focus === "field" ? (danger ? "cancel" : "confirm") : focus;

  useConfirmKeyboard({
    panelRef,
    isActive,
    busy,
    initialFocus: focus,
    defaultAction,
    onCancel,
    onPrimary: () => {
      if (confirmLabel && onConfirm) {
        if (!confirmDisabled) onConfirm();
      } else primaryChoice?.onSelect();
    },
  });

  // Подсказката във футъра следи фокуса: Enter натиска бутона на фокус, а от
  // поле за писане потвърждава Ctrl/Cmd+Enter
  const [focused, setFocused] = useState<{ field: boolean; label: string | null } | null>(null);
  const trackFocus = (target: EventTarget) => {
    if (isEditableTarget(target)) {
      setFocused({ field: true, label: null });
      return;
    }
    const action = target instanceof HTMLElement ? target.closest(`[${ACTION_ATTR}]`) : null;
    setFocused(action ? { field: false, label: action.getAttribute("data-confirm-label") } : null);
  };
  const defaultLabel =
    defaultAction === "confirm"
      ? (confirmLabel ?? null)
      : defaultAction === "cancel"
        ? hideCancel
          ? null
          : cancelText
        : (choices?.find((choice) => choice.key === defaultAction.choice)?.label ?? null);

  const notes = note == null ? [] : Array.isArray(note) ? note : [note];
  const hasPreview = Boolean(preview && preview.items.length > 0);
  const hasBody = hasPreview || notes.length > 0 || children != null || usableChoices.length > 0;
  const padX = isNarrow ? "px-4" : "px-[22px]";
  return (
    <div
      ref={panelRef}
      onFocus={(event) => trackFocus(event.target)}
      onBlur={() => setFocused(null)}
      className="pointer-events-auto flex flex-col overflow-hidden rounded-xl bg-surface shadow-[0_24px_80px_rgba(15,23,42,0.18)]"
    >
      <div className={`flex gap-3.5 ${padX} ${hasBody ? "pb-3.5" : "pb-5"} pt-5`}>
        <span
          aria-hidden
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] text-[19px] ${CHIP_CLASS[intent]}`}
        >
          {icon ?? DEFAULT_ICON[intent]}
        </span>
        <div className="flex min-w-0 flex-col gap-1.5 pt-0.5">
          <h2 className="m-0 text-[17px] font-bold leading-[1.25] tracking-[-0.2px] text-ink">{title}</h2>
          <div className="text-[13.5px] leading-[1.5] text-ink-2">{explanation}</div>
        </div>
      </div>

      {hasBody && (
        <div className={`flex flex-col gap-3 ${padX} pb-4 ${isNarrow ? "" : "pl-[76px]"}`}>
          {preview && hasPreview && <PreviewList preview={preview} />}
          {choices && choices.length > 0 && <ChoiceCards choices={choices} />}
          {children}
          {notes.map((item, index) => (
            <NoteLine key={index} note={item} />
          ))}
        </div>
      )}

      <ConfirmFooter
        isMobile={isMobile}
        isDesktop={isDesktop}
        padX={padX}
        danger={danger}
        busy={busy}
        cancelText={cancelText}
        hideCancel={hideCancel}
        confirmLabel={confirmLabel}
        confirmDisabled={confirmDisabled}
        hint={{
          enterLabel: focused?.field ? null : (focused?.label ?? defaultLabel),
          fieldConfirmLabel: focused?.field ? (confirmLabel ?? null) : null,
        }}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
    </div>
  );
};

export default ConfirmPanel;
